// Memorial analytics event taxonomy.
//
// The transport (e.g. window.gtag) is injected by the host via
// FuneralUIProvider's `analytics={{ track }}` prop, so this package never
// touches window.gtag or process.env directly. `track(eventName, params)` is
// expected to be a no-op when analytics is disabled (the host decides that).
export function createMemorialAnalytics(track) {
  const t = typeof track === "function" ? track : () => {}
  return {
    // `layout` rides along on the view so a provider can be shown how each
    // memorial layout actually performs, not just that the page was opened.
    view: (memorialName, layout) =>
      t("memorial_view", { memorial_name: memorialName, layout }),
    sectionView: sectionName =>
      t("memorial_section_view", { section_name: sectionName }),
    navClick: label => t("memorial_nav_click", { link_label: label }),
    contributeCardClick: cardName =>
      t("memorial_contribute_card_click", { card_name: cardName }),

    storyModalOpen: () => t("memorial_story_modal_open"),
    storyTabChange: tabName =>
      t("memorial_story_tab_change", { tab_name: tabName }),
    storyLoadMore: () => t("memorial_story_load_more"),
    storySubmitted: () => t("memorial_story_submitted"),
    // storySubmitted fires on the ATTEMPT (the click); this fires when the API
    // has actually answered. Both exist so the drop-off between them is
    // visible — with only the attempt, a failing endpoint looks like success.
    // Emitted from useContributions, not the component: `submitted`/`error` are
    // SHARED by stories and gallery there, so a component-level effect watching
    // them would credit a gallery upload to a story.
    storyResult: outcome => t("memorial_story_result", { outcome }),

    galleryModalOpen: () => t("memorial_gallery_modal_open"),
    galleryPhotoSubmitted: () => t("memorial_gallery_photo_submitted"),
    galleryPhotoResult: outcome =>
      t("memorial_gallery_photo_result", { outcome }),
    galleryLightboxOpen: () => t("memorial_gallery_lightbox_open"),
    galleryCarouselNav: direction =>
      t("memorial_gallery_carousel_nav", { direction }),

    donateClick: charityName =>
      t("memorial_donate_click", { charity_name: charityName }),
    sendFlowersClick: () => t("memorial_send_flowers_click"),

    shareClick: platform => t("memorial_share_click", { platform }),

    mapViewClick: () => t("memorial_map_view_click"),

    // ── Live stream ────────────────────────────────────────────────────────
    // liveStreamOpen fires from FuneralDetails when the panel is opened; the
    // rest come from the player. For a livestreamed funeral — often the only
    // way distant family can attend — "did it actually work for them" is the
    // question that matters, and open-only told us nothing about it.
    liveStreamOpen: () => t("memorial_live_stream_open"),
    liveStreamPlay: () => t("memorial_live_stream_play"),
    // Milestones rather than raw progress: a continuous stream of ticks is
    // noise, and GA4 charges per event. Each fires at most once per asset.
    liveStreamProgress: percent =>
      t("memorial_live_stream_progress", { percent }),
    // `recovered` distinguishes a fault the viewer never noticed (there was
    // another recording to advance to) from one that ended their stream. Both
    // are errors; only the second is an incident.
    liveStreamError: recovered =>
      t("memorial_live_stream_error", { recovered }),
    liveStreamEnded: () => t("memorial_live_stream_ended"),

    // ── Memorial chrome ────────────────────────────────────────────────────
    footerLinkClick: label => t("memorial_footer_link_click", { label }),
  }
}
